import { api } from '@/lib/trpc/react';
import { useSetAtom } from 'jotai';
import { showBannerAtom } from '@/lib/atoms/notifications';
import { LOCALE_FIN } from '@/lib/helpers/i18n';
import { useRef } from 'react';
import { detectNewVersion, type SystemEventsProps } from './detect-new-version';

/**
 * The delay before showing the update notification.
 *
 * This is needed as we might be connecting to an old instance during rollout.
 * We don't want to show the notification too early.
 */
export const UPDATE_NOTIFICATION_DELAY = 2 * 60 * 1000; // 2 minutes

export function useSystemEvents(props: SystemEventsProps) {
  const showBanner = useSetAtom(showBannerAtom);
  const updateTimeout = useRef<NodeJS.Timeout | null>(null);

  api.system.onEvent.useSubscription(undefined, {
    onData: (data) => {
      detectNewVersion({
        serverData: data,
        clientData: props,
        timeoutRef: updateTimeout,
        delay: UPDATE_NOTIFICATION_DELAY,
        onNewVersion: () => {
          showBanner({
            message: LOCALE_FIN.SYSTEM_UPDATE_AVAILABLE,
            type: 'info',
            id: 'update-available',
            action: {
              label: LOCALE_FIN.SYSTEM_UPDATE_AVAILABLE_ACTION,
              onClick: () => window.location.reload(),
            },
          });
        },
      });
    },
    onError: () => {
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
        updateTimeout.current = null;
      }
    },
  });
}
